import type { HTMLAttributes, ReactElement } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useIsLobbyButtonHiddenConnected } from '../../../hooks/useIsLobbyButtonHiddenConnected';
import { useRedirectToLobbyCallbackConnected } from '../../../hooks/useRdirectToLobbyCallbackConnected';
import { useShouldStayConnected } from '../../../hooks/useShouldStayConnected';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { SessionTimer } from '../../compliance/SessionTimer';
import { IconButtonWithTooltip } from '../../controls/iconButton/IconButtonWithTooltip';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { Icon, Icons } from '../../icons';
import { PopupType } from '../../popup/types';
import { L12nText } from '../../text/L12nText';
import { Time } from '../../time';
import { Tooltip, TooltipPositions } from '../../tooltip/Tooltip';
import { OverlayLabel } from '../label/OverlayLabel';

import styles from './gameHeader.module.css';

export interface GameHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {}

export const GameHeader = ({ ...rest }: GameHeaderProps): ReactElement | null => {
  const audioTrigger = useAudioTrigger();
  const compliance = useConfigSlice.compliance();
  const dispatch = useCoreDispatch();
  const gameId = useGameStateSlice.gameId();
  const isLobbyButtonHidden = useIsLobbyButtonHiddenConnected();
  const shouldStayAtTable = useShouldStayConnected();
  const tableBetLimits = useConfigSlice.tableBetLimits();
  const tableConfig = useConfigSlice.tableConfig();
  const tableName = useConfigSlice.tableName();
  const open = useHeaderSlice.getCoreBetLimitsOpen();
  const mods = useModifiers(styles, 'gameHeader', { open });
  const redirectToLobby = useRedirectToLobbyCallbackConnected();

  if (!tableName || !tableBetLimits) {
    return null;
  }

  return (
    <div data-testid="game-header" className={`${styles.gameHeader} ${mods}`} {...rest}>
      <div className={styles.gameHeader__row}>
        {!isLobbyButtonHidden && (
          <IconButtonWithTooltip
            data-testid="exit-game-button"
            icon={Icons.Close}
            onClick={(): void => {
              if (shouldStayAtTable) {
                dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
                return;
              }
              redirectToLobby();
            }}
            tooltipOptions={{
              text: 'EXITGAME',
              position: TooltipPositions.Bottom,
            }}
          />
        )}
        <div data-testid="game-header-content" className={styles.gameHeader__content}>
          {compliance?.showSessionId ? (
            <div data-testid="game-header-compliance" className={styles.gameHeader__info_compliance}>
              <OverlayLabel small>
                ID-S: {tableConfig?.aamsSessionId} ID-P: {tableConfig?.aamsTicket}
              </OverlayLabel>
            </div>
          ) : null}
          <button
            className={styles.gameHeader__titleButton}
            onClick={(): void => {
              dispatch(headerActions.setBetLimitsOpen(!open));
              audioTrigger.play({ name: 'menuClick' });
            }}
          >
            <span>
              <L12nText tk={tableName} />
            </span>
            <span>
              <div className={styles.gameHeader__currency}>
                <CurrencyConnected value={tableBetLimits.min} avoidTrailingZero />
              </div>
              {' - '}
              <div className={styles.gameHeader__currency}>
                <CurrencyConnected value={tableBetLimits.max} avoidTrailingZero />
              </div>
            </span>
            <Tooltip
              text={open ? 'CLOSEBETLIMITGSANDPAYOUTS' : 'OPENBETLIMITGSANDPAYOUTS'}
              position={TooltipPositions.Bottom}
            >
              <Icon icon={Icons.ChevronDown} className={styles.gameHeader__icon} />
            </Tooltip>
          </button>
          <div data-testid="game-header-info" className={styles.gameHeader__info}>
            <OverlayLabel small>
              <Time />
            </OverlayLabel>
            <OverlayLabel small>
              <L12nText tk="GAME_NUMBER" />: {gameId}
            </OverlayLabel>
            <OverlayLabel small>{compliance?.showSessionTimer ? <SessionTimer /> : null}</OverlayLabel>
          </div>
        </div>
      </div>
    </div>
  );
};
